// src/pages/ForgotPasswordScreen.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  KeyRound,
  LoaderCircle,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import "../sass/pages/forgotPassword.scss";

import Logo from "../assets/logoClaro.webp";
import { API_BASE_URL } from "../config/api";
import { useToast } from "../context/ToastContext";

const API_BASE = String(
  API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://api.psyassist.com.br"
).replace(/\/+$/, "");

const ENDPOINT_SEND_RESET =
  `${API_BASE}/psy/user/send-reset-code`;

const RESET_STORAGE_KEYS = {
  EMAIL: "psy_password_reset_email",
  REQUESTED_AT: "psy_password_reset_requested_at",
};

const isValidEmail = (value) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(
    String(value || "").trim()
  );
};

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, String(value ?? ""));
    return true;
  } catch {
    return false;
  }
};

const readApiPayload = async (response) => {
  const contentType =
    response.headers.get("content-type") || "";

  try {
    if (contentType.includes("application/json")) {
      const payload = await response.json();

      return payload &&
        typeof payload === "object"
        ? payload
        : {};
    }

    const text = await response.text();

    return text
      ? {
          message: text,
        }
      : {};
  } catch {
    return {};
  }
};

const getRequestErrorMessage = (
  payload,
  status
) => {
  if (status === 429) {
    return (
      payload?.message ||
      "Muitas solicitações foram realizadas. Aguarde alguns minutos antes de tentar novamente."
    );
  }

  if (status >= 500) {
    return "O serviço de recuperação está temporariamente indisponível.";
  }

  if (
    payload?.message &&
    typeof payload.message === "string"
  ) {
    return payload.message;
  }

  return "Não foi possível enviar as instruções de recuperação.";
};

const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const emailInputRef = useRef(null);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] =
    useState("");
  const [loading, setLoading] =
    useState(false);

  const normalizedEmail = useMemo(() => {
    return String(email || "")
      .trim()
      .toLowerCase();
  }, [email]);

  useEffect(() => {
    const rememberedEmail =
      readStorage(
        localStorage,
        "psy_last_email"
      ) ||
      readStorage(
        sessionStorage,
        RESET_STORAGE_KEYS.EMAIL
      );

    if (rememberedEmail) {
      setEmail(rememberedEmail);
      return;
    }

    emailInputRef.current?.focus();
  }, []);

  const validateEmail = () => {
    if (!normalizedEmail) {
      setEmailError(
        "Informe o e-mail utilizado no cadastro."
      );

      return false;
    }

    if (!isValidEmail(normalizedEmail)) {
      setEmailError(
        "Informe um endereço de e-mail válido."
      );

      return false;
    }

    setEmailError("");
    return true;
  };

  const persistResetFlow = () => {
    writeStorage(
      sessionStorage,
      RESET_STORAGE_KEYS.EMAIL,
      normalizedEmail
    );

    writeStorage(
      sessionStorage,
      RESET_STORAGE_KEYS.REQUESTED_AT,
      new Date().toISOString()
    );
  };

  const sendResetCode = async () => {
    if (loading) return;
    if (!validateEmail()) {
      toast.warning({
        title: "Revise o e-mail",
        message:
          "Informe um endereço válido para continuar.",
      });

      window.setTimeout(() => {
        emailInputRef.current?.focus();
      }, 80);

      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        ENDPOINT_SEND_RESET,
        {
          method: "POST",
          headers: {
            "Content-Type":
              "application/json",
            Accept:
              "application/json",
          },
          body: JSON.stringify({
            email: normalizedEmail,
          }),
        }
      );

      const payload =
        await readApiPayload(response);

      if (!response.ok) {
        const requestError =
          new Error(
            getRequestErrorMessage(
              payload,
              response.status
            )
          );

        requestError.status =
          response.status;

        throw requestError;
      }

      persistResetFlow();

      toast.success({
        title: "Instruções enviadas",
        message:
          payload?.message ||
          "Caso exista uma conta para este e-mail, o código será enviado em instantes.",
      });

      navigate("/verify-code", {
        state: {
          email: normalizedEmail,
          requestedAt:
            new Date().toISOString(),
        },
      });
    } catch (error) {
      console.error(
        "Erro ao enviar código de redefinição:",
        error
      );

      const persistent =
        error?.status === 429;

      toast.error({
        title:
          "Não foi possível enviar o código",
        message:
          error?.message ||
          "Verifique sua conexão e tente novamente.",
        duration:
          persistent
            ? 0
            : 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendResetCode();
  };

  const handleBackToLogin = () => {
    if (loading) return;

    navigate("/login");
  };

  return (
    <main className="psy-forgot-page">
      <div
        className="psy-forgot-background"
        aria-hidden="true"
      >
        <span className="psy-forgot-background__orb psy-forgot-background__orb--one" />
        <span className="psy-forgot-background__orb psy-forgot-background__orb--two" />
        <span className="psy-forgot-background__grid" />
      </div>

      <div className="psy-forgot-layout">
        <aside className="psy-forgot-aside">
          <Link
            to="/"
            className="psy-forgot-brand"
            aria-label="Voltar para a página inicial"
          >
            <img
              src={Logo}
              alt="PsyAssist Pro"
            />

            <div>
              <strong>
                PsyAssist Pro
              </strong>

              <span>
                Segurança para seu acesso
              </span>
            </div>
          </Link>

          <div className="psy-forgot-aside__content">
            <span className="psy-forgot-kicker">
              <Sparkles size={15} />
              Recuperação segura
            </span>

            <h1>
              Recupere seu acesso sem
              comprometer sua conta.
            </h1>

            <p>
              Enviaremos um código temporário
              para o e-mail associado ao seu
              cadastro. Nenhuma senha atual
              será exibida ou enviada.
            </p>

            <div className="psy-forgot-assurances">
              <div className="psy-forgot-assurance">
                <span>
                  <KeyRound size={19} />
                </span>

                <div>
                  <strong>
                    Código temporário
                  </strong>

                  <small>
                    A validação possui prazo
                    limitado e deve ser usada
                    somente por você.
                  </small>
                </div>
              </div>

              <div className="psy-forgot-assurance">
                <span>
                  <ShieldCheck size={19} />
                </span>

                <div>
                  <strong>
                    Resposta protegida
                  </strong>

                  <small>
                    O sistema não deve revelar
                    publicamente se um e-mail
                    possui conta.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="psy-forgot-security-card">
            <div>
              <LockKeyhole size={19} />
            </div>

            <p>
              <strong>
                Proteja o código recebido
              </strong>

              O suporte do PsyAssist Pro nunca
              solicitará sua senha ou o código
              completo de recuperação.
            </p>
          </div>
        </aside>

        <section className="psy-forgot-panel">
          <div className="psy-forgot-panel__top">
            <span>
              Lembrou sua senha?
            </span>

            <button
              type="button"
              onClick={handleBackToLogin}
              disabled={loading}
            >
              Entrar
              <ArrowRight size={15} />
            </button>
          </div>

          <div className="psy-forgot-content">
            <header className="psy-forgot-header">
              <div
                className="psy-forgot-header__icon"
                aria-hidden="true"
              >
                <KeyRound size={22} />
              </div>

              <div>
                <span>
                  Recuperação de acesso
                </span>

                <h2>
                  Redefina sua senha
                </h2>

                <p>
                  Informe o e-mail utilizado
                  no cadastro. Na próxima
                  etapa, você confirmará o
                  código recebido.
                </p>
              </div>
            </header>

            <form
              className="psy-forgot-form"
              onSubmit={handleSubmit}
              noValidate
              aria-busy={loading}
            >
              <div
                className={`psy-forgot-field ${
                  emailError
                    ? "psy-forgot-field--error"
                    : ""
                }`}
              >
                <div className="psy-forgot-field__header">
                  <label htmlFor="psy-forgot-email">
                    E-mail cadastrado
                  </label>

                  <span>
                    Obrigatório
                  </span>
                </div>

                <div className="psy-forgot-control">
                  <Mail
                    size={18}
                    aria-hidden="true"
                  />

                  <input
                    ref={emailInputRef}
                    id="psy-forgot-email"
                    name="email"
                    type="email"
                    placeholder="nome@clinica.com.br"
                    value={email}
                    onChange={(event) => {
                      setEmail(
                        event.target.value
                      );

                      if (emailError) {
                        setEmailError("");
                      }
                    }}
                    autoComplete="email"
                    inputMode="email"
                    disabled={loading}
                    aria-invalid={Boolean(
                      emailError
                    )}
                    aria-describedby={
                      emailError
                        ? "psy-forgot-email-error"
                        : "psy-forgot-email-help"
                    }
                  />
                </div>

                {emailError ? (
                  <p
                    id="psy-forgot-email-error"
                    className="psy-forgot-field__error"
                    role="alert"
                  >
                    {emailError}
                  </p>
                ) : (
                  <p
                    id="psy-forgot-email-help"
                    className="psy-forgot-field__help"
                  >
                    Use o mesmo endereço
                    informado na criação da
                    conta.
                  </p>
                )}
              </div>

              <div className="psy-forgot-info">
                <CheckCircle2 size={18} />

                <p>
                  <strong>
                    O que acontece depois?
                  </strong>

                  Você receberá um código de
                  verificação e seguirá para a
                  criação de uma nova senha.
                </p>
              </div>

              <button
                type="submit"
                className="psy-forgot-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <LoaderCircle
                      size={18}
                      className="psy-forgot-spinner"
                    />

                    Enviando instruções...
                  </>
                ) : (
                  <>
                    Enviar código de acesso
                    <ArrowRight size={18} />
                  </>
                )}
              </button>

              <button
                type="button"
                className="psy-forgot-secondary"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                <ArrowLeft size={18} />
                Voltar para o login
              </button>
            </form>

            <section className="psy-forgot-support">
              <Mail size={18} />

              <div>
                <strong>
                  Não encontrou o e-mail?
                </strong>

                <span>
                  Verifique as pastas de spam,
                  promoções e lixo eletrônico
                  antes de solicitar novamente.
                </span>
              </div>
            </section>
          </div>

          <footer className="psy-forgot-footer">
            <ShieldCheck size={14} />

            <p>
              Por segurança, a mensagem de
              confirmação pode ser a mesma para
              e-mails cadastrados e não
              cadastrados.
            </p>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default ForgotPasswordScreen;
